/** Shared API response shapes for the control-plane SPA. */

export type UserRole = "owner" | "admin" | "user";

export type UserProfile = {
  userId: string;
  email: string;
  role: string;
  status: string;
  /** Optional display name (editable in profile). */
  displayName?: string;
};

/** True when role may call /api/v1/admin/* (owner or admin). */
export function isAdminRole(role: string | undefined | null): boolean {
  return role === "owner" || role === "admin";
}

/** Own-user audit event from GET /api/v1/audit. */
export type AuditEvent = {
  id: string;
  at: string;
  action: string;
  accountId?: string;
  tool?: string;
  /** Resolved model id when this was an MCP tool call that used one. */
  model?: string;
  meta?: Record<string, unknown>;
};

export type FlagStatus = "open" | "acked";
export type FlagSeverity = "low" | "medium" | "high";

/** Admin anomaly flag from GET /api/v1/admin/flags. */
export type AdminFlag = {
  flagId: string;
  severity: FlagSeverity | string;
  reason: string;
  createdAt: string;
  status: FlagStatus | string;
  userId?: string;
  ackedAt?: string;
  ackedBy?: string;
  meta?: Record<string, unknown>;
};

/** Invite status from GET /api/v1/admin/invites. */
export type InviteStatus = "pending" | "accepted" | "revoked" | "expired" | string;

/** Admin invite row (never includes raw token). */
export type AdminInvite = {
  id: string;
  email: string;
  role: UserRole | string;
  status: InviteStatus;
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
  resendCount: number;
  acceptedAt?: string;
  acceptedUserId?: string;
};

/** Create/resend response includes one-time inviteUrl + delivery status. */
export type AdminInviteCreated = AdminInvite & {
  inviteUrl: string;
  emailDelivery?: {
    ses?: "sent" | "failed" | "skipped" | string;
    cognito?: "sent" | "failed" | string;
  };
};

/** Admin user pointer from GET /api/v1/admin/users. */
export type AdminUser = {
  userId: string;
  email: string;
  role: UserRole | string;
  status: string;
  createdAt: string;
};

export type AuthChallenge = {
  name: string;
  session: string;
  parameters?: Record<string, string>;
};

export type LoginSuccess = {
  user: UserProfile;
};

export type LoginChallengeResponse = {
  challenge: AuthChallenge;
};

export type LoginResponse = LoginSuccess | LoginChallengeResponse;

export type ProviderId = "xai" | "openai" | "anthropic" | "google";

export type AccountDefaults = {
  model?: string;
  reasoningEffort?: "none" | "low" | "medium" | "high";
};

export type Account = {
  id: string;
  provider: ProviderId | string;
  label: string;
  status: string;
  keyPrefix: string;
  defaults: AccountDefaults;
  capabilities?: Record<string, boolean>;
  lastError?: string;
  lastVerifiedAt?: string;
  createdAt: string;
  updatedAt: string;
};

/** PAT / MCP tool scopes (Phase B). */
export type PatScope = "read" | "send" | "full";

export const PAT_SCOPE_VALUES: readonly PatScope[] = [
  "read",
  "send",
  "full",
] as const;

export type PatMeta = {
  id: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
  expiresAt: string;
  lastUsedAt?: string;
  /**
   * Effective scopes from list/mint.
   * Missing on older servers → treat as `['full']` (Phase A tokens).
   */
  scopes?: PatScope[];
};

export type MintedPat = PatMeta & {
  token: string;
};

/** MCP client connection from GET /api/v1/mcp-clients */
export type McpClient = {
  id: string;
  kind: "pat" | "oauth";
  name: string;
  status: "active" | "revoked" | string;
  createdAt: string;
  lastUsedAt: string;
  scopes?: PatScope[];
  product?: string;
  patTokenId?: string;
};

/** Upload session lifecycle (SPA compose B8). */
export type UploadStatus =
  | "pending"
  | "uploaded"
  | "consumed"
  | "expired"
  | "infected"
  | "aborted";

export type UploadSession = {
  uploadId: string;
  s3Key: string;
  status: UploadStatus;
  createdAt: string;
  expiresAt: string;
  filename?: string;
  contentType?: string;
  size?: number;
};

export type CreateUploadResponse = UploadSession & {
  putUrl: string;
  requiredHeaders: {
    "content-type": string;
    "content-length": string;
  };
  maxBytes: number;
};

/** SPA compose max attachment size (matches server UPLOAD_PUT_MAX_BYTES = 100 MB). */
export const COMPOSE_UPLOAD_MAX_BYTES = 100 * 1024 * 1024;

/**
 * Public feature flags from GET /api/v1/features (B0).
 * Keys match server `FeatureFlags` (dotted names).
 */
export type FeatureFlags = {
  "pats.scopesWrite": boolean;
  "pats.scopesEnforce": boolean;
  "invites.productized": boolean;
  "attachments.presign": boolean;
  "edge.cloudfront": boolean;
  "docs.portal": boolean;
  "abuse.anomalyFlags": boolean;
  "billing.enforce": boolean;
  "billing.ui": boolean;
  "billing.metering": boolean;
  "signup.open": boolean;
  "billing.overage": boolean;
  "billing.dailyCap": boolean;
  "billing.referrals": boolean;
};

/** Client defaults when features endpoint is unavailable (pre-B0 or offline). */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  "pats.scopesWrite": true,
  "pats.scopesEnforce": false,
  "invites.productized": false,
  "attachments.presign": true,
  "edge.cloudfront": false,
  "docs.portal": false,
  "abuse.anomalyFlags": false,
  "billing.enforce": false,
  "billing.ui": true,
  "billing.metering": true,
  "signup.open": true,
  "billing.overage": true,
  "billing.dailyCap": true,
  "billing.referrals": true,
};

export type BillingSummary = {
  subscriptionStatus: string;
  plan: string | null;
  periodEnd: string | null;
  stripeCustomerId: string | null;
  hasStripeSubscription: boolean;
  access: {
    allowed: boolean;
    reason: string;
    softAllow: boolean;
    roleBypass: boolean;
  };
  pricing: {
    monthlyUsd: number;
    annualUsd: number;
    includedBandwidthBytes: number;
    overageUsdPerGb: number;
    dailyAbuseCapBytes?: number;
  };
  stripeConfigured: boolean;
  referralCode: string | null;
  referralLink?: string | null;
  referredByUserId?: string | null;
};

export type PromoRow = {
  code: string;
  type: string;
  value: number;
  maxRedemptions: number | null;
  redemptionCount: number;
  expiresAt: string | null;
  active: boolean;
  createdAt: string;
  note: string | null;
};

export type UsageModelRow = {
  name: string;
  /** Provider tool/chat completions that reported this model. */
  count: number;
  /** Sum of provider-reported total tokens for this model. */
  tokens?: number;
  inputTokens?: number;
  outputTokens?: number;
};

export type UsageWindow = {
  id: "day" | "week" | "month" | "d90";
  label: string;
  from: string;
  to: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  requestCount: number;
  totalBytes?: number;
  mediaBytesIn?: number;
  mediaBytesOut?: number;
  imageCount?: number;
  videoSeconds?: number;
  mcpBytes?: number;
  mediaBytes?: number;
  allBytes?: number;
  models: UsageModelRow[];
  providers?: UsageModelRow[];
  tools?: Array<{ name: string; count: number }>;
};

export type UsageSummary = {
  period: string;
  today: string;
  totalBytes: number;
  mcpBytes?: number;
  mediaBytes?: number;
  mediaBytesIn?: number;
  mediaBytesOut?: number;
  imageCount?: number;
  videoSeconds?: number;
  requestCount: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  includedBandwidthBytes: number;
  usedRatio: number;
  softWarn: boolean;
  overQuota: boolean;
  format: {
    total: string;
    included: string;
    usedGb: number;
    includedGb: number;
    mcp?: string;
    media?: string;
    tokens?: string;
    inputTokens?: string;
    outputTokens?: string;
    images?: string;
    videoSeconds?: string;
  };
  notes?: {
    tokens?: string;
    bandwidth?: string;
  };
  topTools: Array<{ name: string; count: number }>;
  topModels?: UsageModelRow[];
  topProviders?: UsageModelRow[];
  /** Token windows: today, last 7d, calendar month, last 90d. */
  windows?: {
    day: UsageWindow;
    week: UsageWindow;
    month: UsageWindow;
    d90: UsageWindow;
  };
  days: Array<{
    day: string;
    totalBytes: number;
    mcpBytes?: number;
    mediaBytes?: number;
    requestCount: number;
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
    imageCount?: number;
    videoSeconds?: number;
  }>;
};

export function isPatScope(value: unknown): value is PatScope {
  return value === "read" || value === "send" || value === "full";
}

/** Normalize list/mint scopes for UI (missing → full). */
export function scopesOf(pat: { scopes?: unknown }): PatScope[] {
  if (!Array.isArray(pat.scopes) || pat.scopes.length === 0) {
    return ["full"];
  }
  const out: PatScope[] = [];
  for (const s of pat.scopes) {
    if (!isPatScope(s)) continue;
    if (s === "full") return ["full"];
    if (!out.includes(s)) out.push(s);
  }
  return out.length > 0 ? out : ["full"];
}

export type ApiErrorBody = {
  error?: string;
  error_description?: string;
};

/** Pre-login invite preview (GET /api/v1/invites/preview). */
export type InvitePreview = {
  email: string;
  status: string;
  role?: string;
  expiresAt?: string;
  inviteId?: string;
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: unknown;

  constructor(status: number, code: string, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export function isLoginChallenge(
  r: LoginResponse,
): r is LoginChallengeResponse {
  return "challenge" in r && r.challenge != null;
}
